import { useQuery } from '@apollo/client';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { BookingButton, Content, HeaderContainer } from './Appointments.styled';
import { NoAppointment } from './components/NoAppointment/NoAppointment';
import { PastAppointments } from './components/PastAppointments/PastAppointments';
import { UpcomingAppointments } from './components/UpcomingAppointments/UpcomingAppointments';
import { Loader } from 'components/Loader/Loader';
import { Heading } from 'components/v2/Typography/Heading';
import { Appointment } from 'kb-shared';
import { PATIENT_APPOINTMENTS } from 'kb-shared/graphql/queries';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { calculateNextCycleVisitDate } from 'screens/Dashboard/CycleVisit/CycleVisit.mapper';
import { CYCLE_VISIT_DAYS } from 'screens/Dashboard/Dashboard.graphql';
import { CycleVisitDaysResponse } from 'screens/Dashboard/Dashboard.types';
import { ResponsiveContainer } from 'screens/styled_common';
import { pageUrl } from 'utilities/pageUrl';

export const Appointments: React.FC = () => {
  const navigate = useNavigate();

  const { data, loading } = useQuery<{ appointments: Appointment[] }>(PATIENT_APPOINTMENTS, {
    onError: error => BugTracker.notify(error, 'PatientAppointments-Appointments.tsx')
  });
  const { data: cycleVisitData, loading: cycleVisitLoading } = useQuery<CycleVisitDaysResponse>(
    CYCLE_VISIT_DAYS,
    {
      onError: error => BugTracker.notify(error, 'Cycle Visit Days Fetch')
    }
  );
  const nextCycleVisit = calculateNextCycleVisitDate(
    cycleVisitData?.cycleVisitDays,
    cycleVisitData?.cycleVisits
  );
  const hasAppointment = !!data?.appointments.length || nextCycleVisit;

  if (loading || cycleVisitLoading) return <Loader height={150} width={150} absoluteCentered />;

  return (
    <ResponsiveContainer>
      <HeaderContainer>
        <Heading tag="h1" noMargin>
          My Appointments
        </Heading>
        {hasAppointment && (
          <BookingButton
            category="primary"
            label="Schedule Appointment"
            onClick={() => navigate(pageUrl.booking.bookingQuiz())}
            size="md"
          />
        )}
      </HeaderContainer>
      <Content>
        {hasAppointment ? (
          <>
            <UpcomingAppointments
              appointments={data?.appointments}
              nextCycleVisit={nextCycleVisit}
              cycleVisitData={cycleVisitData}
            />
            <PastAppointments appointments={data?.appointments} />
          </>
        ) : (
          <NoAppointment />
        )}
      </Content>
    </ResponsiveContainer>
  );
};
